import { useRef } from 'react';
import { Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import './DoctorCard.css';

const AvatarUploadSlot = ({ avatar, name, uploading, onSelectFile }) => {
  const inputRef = useRef(null);

  const handleChange = (e) => {
    const file = e.target.files?.[0];
    if (file) onSelectFile?.(file);
    e.target.value = '';
  };

  return (
    <div className="doctor-card__avatar-upload">
      {avatar ? (
        <img src={avatar} alt={name || 'Doctor'} className="doctor-card__avatar" />
      ) : (
        <div className="doctor-card__avatar-placeholder">
          <span>{uploading ? '…' : 'Photo'}</span>
        </div>
      )}
      <label className="doctor-card__avatar-upload-btn">
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          className="visually-hidden"
          onChange={handleChange}
          disabled={uploading}
        />
        {avatar ? 'Change' : 'Upload'}
      </label>
    </div>
  );
};

export const DoctorCard = ({
  slug,
  id,
  avatar,
  name,
  role,
  hospital,
  hospitalName,
  location,
  specializes,
  onViewProfile,
  hideActions = false,
  allowImageUpload = false,
  onUploadAvatar,
  uploadingAvatar = false,
}) => {
  const navigate = useNavigate();

  const handleViewProfile = () => {
    if (onViewProfile) {
      onViewProfile(id);
      return;
    }
    const pathSlug = slug || id;
    if (pathSlug) navigate(`/doctors/${pathSlug}`);
  };

  const resolvedHospital = hospitalName?.trim() || null;
  const resolvedLocation = location?.trim() || null;
  const hasLocation = resolvedHospital || resolvedLocation || (hospital && hospital !== '—');

  return (
    <div className="doctor-card">
      <div className="doctor-card__header">
        {allowImageUpload ? (
          <AvatarUploadSlot
            avatar={avatar}
            name={name}
            uploading={uploadingAvatar}
            onSelectFile={onUploadAvatar}
          />
        ) : (
          <img src={avatar} alt={name} className="doctor-card__avatar" />
        )}
        <div className="doctor-card__header-text">
          {name ? <p className="doctor-card__name">{name}</p> : null}
          {role ? <p className="doctor-card__role">{role}</p> : null}
        </div>
      </div>

      <div className="doctor-card__body">
        {hasLocation ? (
          <div className="doctor-card__info-row">
            <svg className="doctor-card__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0 1 18 0z" />
              <circle cx="12" cy="10" r="3" />
            </svg>
            <span className="doctor-card__location-text">
              {resolvedHospital && (
                <span className="doctor-card__hospital">{resolvedHospital}</span>
              )}
              {resolvedHospital && resolvedLocation && (
                <span className="doctor-card__location-sep">, </span>
              )}
              {resolvedLocation && (
                <span className="doctor-card__city">{resolvedLocation}</span>
              )}
              {!resolvedHospital && !resolvedLocation && hospital && (
                <span>{hospital}</span>
              )}
            </span>
          </div>
        ) : null}

        {specializes ? (
          <div className="doctor-card__info-row doctor-card__info-row--top">
            <svg className="doctor-card__icon doctor-card__icon--top" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="7" width="20" height="14" rx="2" />
              <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" />
            </svg>
            <span><strong>Specializes in</strong> : {specializes}</span>
          </div>
        ) : null}

        {!hideActions && (
          <>
            <Button className="doctor-card__btn-primary w-100">
              Send Medical Query
            </Button>
            <Button
              variant="outline-secondary"
              className="doctor-card__btn-outline w-100"
              onClick={handleViewProfile}
            >
              View Profile
            </Button>
          </>
        )}
      </div>
    </div>
  );
};
